import { CssBaseline } from "@mui/material";
import {
	createTheme,
	StyledEngineProvider,
	ThemeOptions,
	ThemeProvider as MUIThemeProvider
} from "@mui/material/styles";
import { ReactNode, useMemo } from "react";

import useThemeStore from "../contexts/theme/store";
import ThemeContext from "../contexts/theme/themeContext";
import breakpoints from "../theme/breakpoints";
import componentsOverride from "../theme/overrides";
import palette from "../theme/palette";
import shadows, { customShadows } from "../theme/shadows";
import typography from "../theme/typography";

// ----------------------------------------------------------------------

type Props = {
	children: ReactNode;
};

export default function ThemeProvider({ children }: Props) {
	const [state, actions] = useThemeStore();

	const isLight = state.themeMode === "light";

	const themeOptions: ThemeOptions = useMemo(
		() => ({
			palette: isLight ? palette.light : palette.dark,
			typography,
			breakpoints,
			shape: { borderRadius: 8 },
			direction: "ltr",
			shadows: isLight ? shadows.light : shadows.dark,
			customShadows: isLight ? customShadows.light : customShadows.dark
		}),
		[isLight]
	);

	const theme = createTheme(themeOptions);
	theme.components = componentsOverride(theme);

	return (
		<ThemeContext.Provider value={{ actions, state }}>
			<StyledEngineProvider injectFirst>
				<MUIThemeProvider theme={theme}>
					<CssBaseline />
					{children}
				</MUIThemeProvider>
			</StyledEngineProvider>
		</ThemeContext.Provider>
	);
}
